import type { NodeKind, NodeDataMap, FlowNode } from './types.js';

export type HandleSpec = { id: string; label: string; tone?: 'default' | 'success' | 'danger' };

export interface NodeSpec<K extends NodeKind = NodeKind> {
  kind: K;
  title: string;
  description: string;
  category: 'Trigger' | 'Data' | 'Logic' | 'Transform' | 'Security' | 'Output' | 'Utility';
  /** lucide-react icon name */
  icon: string;
  accent: string;
  /** Output handles. A node with more than one is a branching node. */
  outputs: HandleSpec[];
  hasInput: boolean;
  /** At most one of these may exist per flow. */
  singleton?: boolean;
  defaults: () => NodeDataMap[K];
}

function spec<K extends NodeKind>(s: NodeSpec<K>): NodeSpec<K> {
  return s;
}

const OUT: HandleSpec[] = [{ id: 'out', label: 'Next' }];

export const NODE_SPECS = {
  'trigger.http': spec({
    kind: 'trigger.http',
    title: 'HTTP Trigger',
    description: 'Entry point. Defines the method, path and how callers authenticate.',
    category: 'Trigger',
    icon: 'Webhook',
    accent: '#6366f1',
    outputs: OUT,
    hasInput: false,
    singleton: true,
    defaults: () => ({
      method: 'GET',
      path: '/hello',
      authMode: 'token',
      minRole: 'viewer',
      scopes: ['api:invoke'],
      query: [],
      body: [],
      description: '',
    }),
  }),

  'auth.guard': spec({
    kind: 'auth.guard',
    title: 'Auth Guard',
    description: 'Stops the request unless the caller has the required role, scopes or expression.',
    category: 'Security',
    icon: 'ShieldCheck',
    accent: '#f43f5e',
    outputs: OUT,
    hasInput: true,
    defaults: () => ({
      minRole: 'editor',
      scopes: [],
      expression: '',
      denyStatus: 403,
      denyMessage: 'Forbidden',
    }),
  }),

  'validate.schema': spec({
    kind: 'validate.schema',
    title: 'Validate',
    description: 'Type-checks and coerces incoming fields, rejecting bad requests early.',
    category: 'Security',
    icon: 'CheckCheck',
    accent: '#0ea5e9',
    outputs: OUT,
    hasInput: true,
    defaults: () => ({
      target: 'body',
      fields: [],
      stripUnknown: true,
      failStatus: 422,
      expression: '',
    }),
  }),

  'datasource.query': spec({
    kind: 'datasource.query',
    title: 'Database Query',
    description: 'Runs a parameterised SQL query against a connected datasource.',
    category: 'Data',
    icon: 'Database',
    accent: '#10b981',
    outputs: OUT,
    hasInput: true,
    defaults: () => ({
      datasourceId: null,
      sql: 'SELECT 1 AS ok',
      params: {},
      single: false,
      timeoutMs: 10000,
    }),
  }),

  'http.request': spec({
    kind: 'http.request',
    title: 'HTTP Request',
    description: 'Calls an external REST API and captures the response.',
    category: 'Data',
    icon: 'Globe',
    accent: '#14b8a6',
    outputs: OUT,
    hasInput: true,
    defaults: () => ({
      method: 'GET',
      url: 'https://api.example.com/items',
      headers: {},
      query: {},
      bodyMode: 'none',
      body: '',
      timeoutMs: 10000,
      failOnError: true,
    }),
  }),

  'transform.js': spec({
    kind: 'transform.js',
    title: 'JavaScript',
    description: 'Sandboxed JS for arbitrary shaping. Receives input, steps, state, user.',
    category: 'Transform',
    icon: 'Code2',
    accent: '#eab308',
    outputs: OUT,
    hasInput: true,
    defaults: () => ({
      code: [
        '// `steps` holds every previous node output keyed by node id.',
        '// Return the value this node should output.',
        'return { ok: true, receivedAt: new Date().toISOString() };',
      ].join('\n'),
    }),
  }),

  'transform.map': spec({
    kind: 'transform.map',
    title: 'Map Fields',
    description: 'Reshapes an object or array without writing code.',
    category: 'Transform',
    icon: 'Shuffle',
    accent: '#a855f7',
    outputs: OUT,
    hasInput: true,
    defaults: () => ({
      source: '{{ steps.previous }}',
      mode: 'array',
      rules: [],
      keepUnmapped: false,
    }),
  }),

  'logic.condition': spec({
    kind: 'logic.condition',
    title: 'Condition',
    description: 'Branches the flow on a set of comparisons.',
    category: 'Logic',
    icon: 'GitBranch',
    accent: '#f97316',
    outputs: [
      { id: 'true', label: 'True', tone: 'success' },
      { id: 'false', label: 'False', tone: 'danger' },
    ],
    hasInput: true,
    defaults: () => ({ combinator: 'and', conditions: [] }),
  }),

  'logic.switch': spec({
    kind: 'logic.switch',
    title: 'Switch',
    description: 'Routes to the first matching case, with an optional default branch.',
    category: 'Logic',
    icon: 'Split',
    accent: '#fb923c',
    outputs: [{ id: 'default', label: 'Default' }],
    hasInput: true,
    defaults: () => ({ cases: [], hasDefault: true }),
  }),

  'logic.foreach': spec({
    kind: 'logic.foreach',
    title: 'For Each',
    description: 'Runs the branch once per item and collects the results.',
    category: 'Logic',
    icon: 'Repeat',
    accent: '#fbbf24',
    outputs: [
      { id: 'body', label: 'Each item' },
      { id: 'done', label: 'Done', tone: 'success' },
    ],
    hasInput: true,
    defaults: () => ({ source: '{{ steps.previous }}', itemName: 'item', maxIterations: 100, concurrency: 1 }),
  }),

  'state.set': spec({
    kind: 'state.set',
    title: 'Set Variables',
    description: 'Stores values in flow state for later nodes to read.',
    category: 'Utility',
    icon: 'Variable',
    accent: '#64748b',
    outputs: OUT,
    hasInput: true,
    defaults: () => ({ assignments: [] }),
  }),

  'util.delay': spec({
    kind: 'util.delay',
    title: 'Delay',
    description: 'Pauses the flow. Useful for rate-limited upstreams.',
    category: 'Utility',
    icon: 'Timer',
    accent: '#94a3b8',
    outputs: OUT,
    hasInput: true,
    defaults: () => ({ ms: 250 }),
  }),

  'util.log': spec({
    kind: 'util.log',
    title: 'Log',
    description: 'Writes a message into the run trace.',
    category: 'Utility',
    icon: 'ScrollText',
    accent: '#94a3b8',
    outputs: OUT,
    hasInput: true,
    defaults: () => ({ level: 'info', message: 'checkpoint' }),
  }),

  'realtime.publish': spec({
    kind: 'realtime.publish',
    title: 'Publish',
    description: 'Broadcasts a message to a project channel that dashboards subscribe to.',
    category: 'Utility',
    icon: 'Radio',
    accent: '#06b6d4',
    outputs: OUT,
    hasInput: true,
    defaults: () => ({ channel: 'events', payload: '{{ steps.previous }}', retain: true }),
  }),

  response: spec({
    kind: 'response',
    title: 'Response',
    description: 'Terminates the flow and sends the HTTP response.',
    category: 'Output',
    icon: 'Send',
    accent: '#22c55e',
    outputs: [],
    hasInput: true,
    defaults: () => ({
      status: 200,
      bodyMode: 'passthrough',
      body: '{{ steps.previous }}',
      headers: {},
      schema: [],
    }),
  }),
} satisfies { [K in NodeKind]: NodeSpec<K> };

export type NodeSpecMap = typeof NODE_SPECS;

export function getNodeSpec<K extends NodeKind>(kind: K): NodeSpec<K> {
  return NODE_SPECS[kind] as unknown as NodeSpec<K>;
}

export const NODE_CATEGORIES = [
  'Trigger',
  'Security',
  'Data',
  'Transform',
  'Logic',
  'Utility',
  'Output',
] as const;

export function nodeSpecList(): NodeSpec[] {
  return Object.values(NODE_SPECS) as unknown as NodeSpec[];
}

/** Builds a fresh node of the given kind at a canvas position. */
export function createNode<K extends NodeKind>(
  kind: K,
  position: { x: number; y: number },
  id: string,
): FlowNode {
  const s = getNodeSpec(kind);
  return {
    id,
    type: kind,
    position,
    label: s.title,
    data: s.defaults(),
  } as FlowNode;
}

/** Output handles for a node, accounting for dynamic handles (switch cases). */
export function outputsFor(node: FlowNode): HandleSpec[] {
  if (node.type === 'logic.switch') {
    const cases = node.data.cases.map((c) => ({ id: c.id, label: c.label || 'Case' }));
    return node.data.hasDefault ? [...cases, { id: 'default', label: 'Default' }] : cases;
  }
  return getNodeSpec(node.type).outputs;
}
